import axios from 'axios';

import { buildAuthHeaders } from '../utils/authHeaders';
import { getImportSourceLabel } from '../utils/tradeImport';

const TAX_TOTAL_FIELDS = [
  'fnoProfit',
  'fnoLoss',
  'fnoNet',
  'intradayProfit',
  'intradayLoss',
  'intradayNet',
  'deliveryBusinessProfit',
  'deliveryBusinessLoss',
  'deliveryBusinessNet',
  'businessIncomeBeforeTaxDepreciation',
  'taxDepreciation',
  'businessIncomeAfterTaxDepreciation',
  'dividendIncome',
  'interestIncome',
  'otherSourcesIncome',
  'otherIncome',
  'shortTermCapitalGain',
  'longTermCapitalGainGross',
  'longTermCapitalGainThresholdRelief',
  'longTermCapitalGain',
  'grossTotalIncome',
];

const uniqueValues = (values = []) => [...new Set(values.filter(Boolean))];

const toAmount = (value) => Number(value ?? 0);

const buildCombinedRequestError = (error, fallbackMessage) => {
  const validationMessage = Array.isArray(error?.response?.data)
    ? error.response.data[0]?.message
    : error?.response?.data?.message;

  return new Error(validationMessage || error?.message || fallbackMessage);
};

const buildCombinedReportRequest = (clientIds, financialYear, options = {}) => ({
  clientIds,
  financialYear,
  allAccessibleClients: Boolean(options.allAccessibleClients),
});

const fetchCombinedReport = async (apiUrl, endpoint, clientIds, financialYear, fallbackMessage, options = {}) => {
  try {
    const response = await axios.post(
      `${apiUrl}/api/reports/combined/${endpoint}`,
      buildCombinedReportRequest(clientIds, financialYear, options),
      {
        headers: buildAuthHeaders(options.accessToken),
      },
    );

    return response.data;
  } catch (error) {
    throw buildCombinedRequestError(error, fallbackMessage);
  }
};

const normalizeCombinedCapitalGains = (capitalGains) => ({
  ...capitalGains,
  ltcg: (capitalGains?.ltcg || []).map((row, index) => ({
    ...row,
    combinedRowId: `${row.clientId || 'combined'}-ltcg-${row.assetName || 'asset'}-${index}`,
  })),
  stcg: (capitalGains?.stcg || []).map((row, index) => ({
    ...row,
    combinedRowId: `${row.clientId || 'combined'}-stcg-${row.assetName || 'asset'}-${index}`,
  })),
});

const normalizeCombinedTaxComputation = (report) => ({
  ...report,
  manualIncomeEntries: (report?.manualIncomeEntries || []).map((entry, index) => ({
    ...entry,
    combinedRowId: `${entry.clientId || 'combined'}-${entry.taxHead || 'tax'}-${entry.label || 'line'}-${index}`,
  })),
  depreciationSchedule: report?.depreciationSchedule
    ? {
      ...report.depreciationSchedule,
      blocks: (report.depreciationSchedule.blocks || []).map((block, index) => ({
        ...block,
        combinedRowId: `${block.clientId || 'combined'}-${block.taxBlock || 'block'}-${index}`,
      })),
    }
    : report?.depreciationSchedule,
});

const mergeNestedAmountMaps = (reports, fieldName) => {
  const merged = {};

  reports.forEach(({ data }) => {
    Object.entries(data?.[fieldName] || {}).forEach(([sectionLabel, rows]) => {
      if (!merged[sectionLabel]) {
        merged[sectionLabel] = {};
      }

      Object.entries(rows || {}).forEach(([rowLabel, amount]) => {
        merged[sectionLabel][rowLabel] = toAmount(merged[sectionLabel][rowLabel]) + toAmount(amount);
      });
    });
  });

  return merged;
};

const sortCapitalGainRows = (rows) => [...rows].sort(
  (left, right) => new Date(right.transferDate || 0).getTime() - new Date(left.transferDate || 0).getTime(),
);

const aggregatePeriodRows = (rows) => {
  const totals = new Map();

  rows.forEach((row) => {
    const key = row?.name || 'Other';
    totals.set(key, toAmount(totals.get(key)) + toAmount(row?.amount));
  });

  return [...totals.entries()]
    .map(([name, amount]) => ({ name, amount }))
    .sort((left, right) => toAmount(right.amount) - toAmount(left.amount));
};

export const buildCombinedClientOptions = (profile, clients = []) => {
  const profileOptions = profile?.clientAccesses?.length > 0
    ? profile.clientAccesses.map((clientAccess) => ({
      value: clientAccess.clientId,
      brokerPreset: clientAccess.brokerPreset || null,
      label: `${clientAccess.clientId} - ${clientAccess.brokerPreset ? getImportSourceLabel(clientAccess.brokerPreset).replace(' preset', '') : 'No preset'}`,
    }))
    : profile?.clientIds?.map((clientId) => ({
      value: clientId,
      brokerPreset: null,
      label: `${clientId} - No preset`,
    }));

  const fallbackOptions = (clients || []).map((client) => ({
    value: client.id,
    brokerPreset: null,
    label: `${client.name || client.id} - No preset`,
  }));

  const rawOptions = profileOptions?.length > 0 ? profileOptions : fallbackOptions;
  const seen = new Set();

  return rawOptions.filter((option) => {
    if (!option?.value || seen.has(option.value)) {
      return false;
    }
    seen.add(option.value);
    return true;
  });
};

export const resolveEffectiveClientIds = ({ clientScopeMode, selectedClientIds, availableClientIds }) => {
  if (clientScopeMode === 'selected') {
    return uniqueValues(selectedClientIds).filter((clientId) => availableClientIds.includes(clientId));
  }

  return uniqueValues(availableClientIds);
};

export const mergeProfitLossReports = (reports) => ({
  Revenue: mergeNestedAmountMaps(reports, 'Revenue'),
  Expenses: mergeNestedAmountMaps(reports, 'Expenses'),
  totalRevenue: reports.reduce((sum, { data }) => sum + toAmount(data?.totalRevenue), 0),
  totalExpense: reports.reduce((sum, { data }) => sum + toAmount(data?.totalExpense), 0),
});

export const mergeBalanceSheetReports = (reports) => ({
  Assets: mergeNestedAmountMaps(reports, 'Assets'),
  Liabilities: mergeNestedAmountMaps(reports, 'Liabilities'),
  totalAssets: reports.reduce((sum, { data }) => sum + toAmount(data?.totalAssets), 0),
  totalLiabilities: reports.reduce((sum, { data }) => sum + toAmount(data?.totalLiabilities), 0),
});

export const mergeCapitalGainBundles = (bundles) => ({
  ltcg: sortCapitalGainRows(
    bundles.flatMap(({ clientId, capitalGains }) =>
      (capitalGains?.ltcg || []).map((row, index) => ({
        ...row,
        clientId,
        combinedRowId: `${clientId}-ltcg-${row.assetName || 'asset'}-${index}`,
      }))),
  ),
  ltcgTotal: bundles.reduce((sum, { capitalGains }) => sum + toAmount(capitalGains?.ltcgTotal), 0),
  ltcgTaxableTotal: bundles.reduce((sum, { capitalGains }) => sum + toAmount(capitalGains?.ltcgTaxableTotal), 0),
  ltcgThresholdRelief: bundles.reduce((sum, { capitalGains }) => sum + toAmount(capitalGains?.ltcgThresholdRelief), 0),
  stcg: sortCapitalGainRows(
    bundles.flatMap(({ clientId, capitalGains }) =>
      (capitalGains?.stcg || []).map((row, index) => ({
        ...row,
        clientId,
        combinedRowId: `${clientId}-stcg-${row.assetName || 'asset'}-${index}`,
      }))),
  ),
  stcgTotal: bundles.reduce((sum, { capitalGains }) => sum + toAmount(capitalGains?.stcgTotal), 0),
  stcgTaxableTotal: bundles.reduce((sum, { capitalGains }) => sum + toAmount(capitalGains?.stcgTaxableTotal), 0),
  stcgPeriod: aggregatePeriodRows(bundles.flatMap((bundle) => bundle.stcgPeriod || [])),
  ltcgPeriod: aggregatePeriodRows(bundles.flatMap((bundle) => bundle.ltcgPeriod || [])),
});

export const mergeTaxComputationReports = (reports, financialYear) => {
  const merged = {
    clientId: reports.length === 1 ? reports[0].clientId : 'Combined',
    financialYear,
    manualIncomeEntries: [],
    depreciationSchedule: {
      clientId: reports.length === 1 ? reports[0].clientId : 'Combined',
      financialYear,
      totalTaxDepreciation: 0,
      totalClosingWdv: 0,
      blocks: [],
    },
  };

  TAX_TOTAL_FIELDS.forEach((fieldName) => {
    merged[fieldName] = reports.reduce((sum, { data }) => sum + toAmount(data?.[fieldName]), 0);
  });

  merged.manualIncomeEntries = reports.flatMap(({ clientId, data }) =>
    (data?.manualIncomeEntries || []).map((entry, index) => ({
      ...entry,
      clientId,
      combinedRowId: `${clientId}-${entry.taxHead || 'tax'}-${entry.label || 'line'}-${index}`,
    })))
    .sort((left, right) => {
      const clientOrder = left.clientId.localeCompare(right.clientId);
      if (clientOrder !== 0) {
        return clientOrder;
      }
      return (left.label || '').localeCompare(right.label || '');
    });

  merged.depreciationSchedule.totalTaxDepreciation = reports.reduce(
    (sum, { data }) => sum + toAmount(data?.depreciationSchedule?.totalTaxDepreciation),
    0,
  );
  merged.depreciationSchedule.totalClosingWdv = reports.reduce(
    (sum, { data }) => sum + toAmount(data?.depreciationSchedule?.totalClosingWdv),
    0,
  );
  merged.depreciationSchedule.blocks = reports.flatMap(({ clientId, data }) =>
    (data?.depreciationSchedule?.blocks || []).map((block, index) => ({
      ...block,
      clientId,
      combinedRowId: `${clientId}-${block.taxBlock || 'block'}-${index}`,
    })))
    .sort((left, right) => {
      const clientOrder = left.clientId.localeCompare(right.clientId);
      if (clientOrder !== 0) {
        return clientOrder;
      }
      return (left.taxBlock || '').localeCompare(right.taxBlock || '');
    });

  return merged;
};

export const fetchCombinedProfitLoss = async (apiUrl, clientIds, financialYear, options = {}) => {
  return fetchCombinedReport(
    apiUrl,
    'profit-loss',
    clientIds,
    financialYear,
    'Unable to load the combined profit and loss report.',
    options,
  );
};

export const fetchCombinedBalanceSheet = async (apiUrl, clientIds, financialYear, options = {}) => {
  return fetchCombinedReport(
    apiUrl,
    'balance-sheet',
    clientIds,
    financialYear,
    'Unable to load the combined balance sheet.',
    options,
  );
};

export const fetchCombinedCapitalGainReport = async (apiUrl, clientIds, financialYear, options = {}) => {
  const capitalGains = await fetchCombinedReport(
    apiUrl,
    'capital-gains',
    clientIds,
    financialYear,
    'Unable to load the combined capital gains report.',
    options,
  );

  return normalizeCombinedCapitalGains(capitalGains);
};

export const fetchCombinedTaxComputation = async (apiUrl, clientIds, financialYear, options = {}) => {
  const report = await fetchCombinedReport(
    apiUrl,
    'tax-computation',
    clientIds,
    financialYear,
    'Unable to load the combined tax computation report.',
    options,
  );

  return normalizeCombinedTaxComputation(report);
};

export const fetchCombinedOverview = async (apiUrl, clientIds, financialYear, options = {}) => {
  const response = await fetchCombinedReport(
    apiUrl,
    'overview',
    clientIds,
    financialYear,
    'Unable to load the combined overview.',
    options,
  );

  return {
    ...response,
    capitalGains: normalizeCombinedCapitalGains(response.capitalGains),
    taxComputation: normalizeCombinedTaxComputation(response.taxComputation),
  };
};

export const resolveCombinedReportError = (error, fallbackMessage) => error?.message || fallbackMessage;